import {settleExistingSection} from '../../core/actionHighlighter';

/**
 * The Maintenance tab each section opens, by the label Canvas renders.
 *
 * Read from the label rather than driven by a bespoke CustomEvent, because
 * `Maintenance.jsx` already marks every tab with `data-agent-section` set to
 * that label and `aria-selected` - which is the whole of what
 * `settleExistingSection` needs. Adding an event per tab would mean a
 * listener per tab in a component that already has eight of them, for no
 * behaviour the attributes do not already provide.
 *
 * The label is the exact string in the tab list, `System Health(Infra)`
 * included: it is matched, not displayed, so tidying it here would only
 * stop it matching.
 */
const TAB_LABEL = {
	'maintenance.network_health_center': 'Network Health Center',
	'maintenance.system_health': 'System Health(Infra)',
	'maintenance.firmware_management': 'Firmware Management',
	'maintenance.troubleshooting': 'Troubleshooting',
};

/**
 * Open one Maintenance tab and settle once it is the one showing.
 *
 * Three of these four tabs are conditional - System Health and
 * Troubleshooting on being an administrator, Network Health Center on a
 * feature flag - so "not found" is a normal outcome rather than a fault,
 * and it is reported as the section being unavailable so the reply says the
 * tab is not there instead of claiming it opened.
 *
 * @param {string} sectionId agent section id
 * @return {Promise<void>} resolves once the tab is current
 */
async function openMaintenanceTab(sectionId) {
	const label = TAB_LABEL[sectionId];
	if (!label) throw new Error('maintenance_tab_unknown');
	try {
		// Only what the DOM check establishes: this tab is the one
		// showing. The section id came from the command, so reporting it
		// back as what was seen would dress an echo as an observation.
		return await settleExistingSection(label);
	} catch (_) {
		throw new Error(`${sectionId.replace('.', '_')}_unavailable`);
	}
}

/**
 * Open the Network Health Center tab.
 * @return {Promise<void>} completion
 */
export function openNetworkHealthCenterSection() {
	return openMaintenanceTab('maintenance.network_health_center');
}

/**
 * Open the System Health(Infra) tab.
 * @return {Promise<void>} completion
 */
export function openSystemHealthSection() {
	return openMaintenanceTab('maintenance.system_health');
}

/**
 * Open the Firmware Management tab.
 * @return {Promise<void>} completion
 */
export function openFirmwareManagementSection() {
	return openMaintenanceTab('maintenance.firmware_management');
}

/**
 * Open the Troubleshooting tab.
 * @return {Promise<void>} completion
 */
export function openTroubleshootingSection() {
	return openMaintenanceTab('maintenance.troubleshooting');
}
